// Affichage des lieux d'une mission de dispatch : lignes de texte + URL
// Google Maps adaptée au contenu (coordonnées GPS, adresse postale, label de
// point d'intérêt, ou texte brut importé d'e-mail). Factorise la logique
// dupliquée getLieu/getDestination de l'ancien DepDispatchATraiter.
const MAPS = "https://www.google.com/maps?q="

export function getLieuAffichage(po, fallbacks = []) {
	const a = (po && po.adresse) || {}
	const extra = a.extra_data || {}

	// Coordonnées GPS (POI type Coordonnées).
	if (extra.latitude && extra.longitude) {
		return {
			lines: [po.label || `${extra.latitude}, ${extra.longitude}`],
			mapsUrl: `${MAPS}${extra.latitude},${extra.longitude}`,
		}
	}

	const adresseParts = [
		[a.rue, a.numero].filter(Boolean).join(" "),
		[a.code_postal, a.localite].filter(Boolean).join(" "),
		a.pays,
	].filter(Boolean)
	const mapsAdresse =
		a.rue && a.localite
			? `${MAPS}${encodeURIComponent(adresseParts.join(", "))}`
			: null

	// Point d'intérêt nommé (parc, fourrière, garage…).
	if (po && po.label) {
		return {
			lines: [po.label],
			mapsUrl: mapsAdresse || `${MAPS}${encodeURIComponent(po.label)}`,
		}
	}

	// Adresse postale saisie.
	if (adresseParts.length > 0) {
		return { lines: adresseParts, mapsUrl: mapsAdresse }
	}

	// Texte brut (import e-mail : champs _0/_1/_2 de la mission).
	const fb = (fallbacks || []).filter(Boolean)
	if (fb.length > 0) {
		return {
			lines: fb,
			mapsUrl: `${MAPS}${encodeURIComponent(fb.join(", "))}`,
		}
	}
	return null
}

// ─── Carte « Trajet » de la fiche dépannage (refonte 06.10) ───

// Types de POI (miroir PointInteretComponent / modèle PointInteret).
const TYPE_LABELS = {
	0: "Parc",
	1: "Fourrière",
	4: "Adresse",
	6: "GPS",
	7: "Parc police",
	8: "Atelier",
}

// Miroir de cle_geo (backend/svp/lieux_geocodage.py) : l'adresse pour
// laquelle `extra_data.geo` a été calculé. Une adresse modifiée depuis
// (édition en cours, pas encore sauvée) rend le résultat périmé.
export const cleGeo = (a) =>
	["rue", "numero", "code_postal", "localite", "pays"]
		.map((c) =>
			String(a[c] ?? "")
				.trim()
				.toLowerCase(),
		)
		.join("|")

// Coordonnées affichables d'un POI, ou null : latitude/longitude à la racine
// d'extra_data (POI GPS, parcs / fourrières géocodés), sinon le géocodage
// d'arrière-plan des adresses (`geo`) s'il correspond encore à l'adresse.
export const coordsCarte = (po) => {
	const a = (po && po.adresse) || {}
	const extra = a.extra_data || {}
	let lat = extra.latitude
	let lng = extra.longitude
	if (!(lat && lng)) {
		const geo = extra.geo || {}
		if (!(geo.latitude && geo.longitude) || geo.cle !== cleGeo(a))
			return null
		lat = geo.latitude
		lng = geo.longitude
	}
	lat = parseFloat(lat)
	lng = parseFloat(lng)
	return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null
}

// Adresse que le géocodage serveur n'a pas su placer (P10) — à jour avec
// l'adresse affichée : on dit pourquoi le point manque sur la carte.
export const positionIntrouvable = (po) => {
	const a = (po && po.adresse) || {}
	const geo = (a.extra_data || {}).geo || {}
	return !!geo.introuvable && geo.cle === cleGeo(a) && !coordsCarte(po)
}

const MOTIF_COORDS = /^\s*-?\d{1,3}(\.\d+)?\s*,\s*-?\d{1,3}(\.\d+)?\s*$/

// Adresse reverse-géocodée d'un POI GPS, en lignes (rue n° / NPA localité) :
// `extra_data.adresse_extrapolee` posée à l'habillage (cron-camions), sinon
// — points habillés avant le 08.10 — le label découpé au premier « , ».
// [] si rien (label encore brut « lat, lng »).
export const adresseExtrapolee = (po) => {
	const extra = ((po && po.adresse) || {}).extra_data || {}
	const ex = extra.adresse_extrapolee
	if (ex && (ex.ligne_1 || ex.ligne_2))
		return [ex.ligne_1, ex.ligne_2].filter(Boolean)
	const label = (po && po.label) || ""
	if (!label || MOTIF_COORDS.test(label)) return []
	const i = label.indexOf(",")
	if (i < 0) return [label.trim()]
	return [label.slice(0, i), label.slice(i + 1)]
		.map((l) => l.trim())
		.filter(Boolean)
}

// Bloc d'adresse riche (lecture seule) : pastille de type, label en gras,
// adresse sur 2 lignes (rue n° / NPA localité), URL Maps. null si le POI
// n'a jamais été saisi. Tolère la base dep legacy {ligne_0..2}.
export const getLieuCarte = (po) => {
	if (!po) return null
	const affichage = getLieuAffichage(po, [po.ligne_0, po.ligne_1, po.ligne_2])
	if (!affichage) return null
	const a = po.adresse || {}
	const extra = a.extra_data || {}
	const coordsTexte =
		extra.latitude && extra.longitude
			? `${extra.latitude}, ${extra.longitude}`
			: ""
	// Un POI GPS (type 6) : titre = coordonnées, puis l'adresse estimée par
	// le reverse geocoding sur deux lignes (cf. adresseExtrapolee).
	if (po.type === 6 && coordsTexte) {
		const estimee = adresseExtrapolee(po)
		return {
			type: TYPE_LABELS[6],
			titre: coordsTexte,
			lignes: estimee,
			estimee: estimee.length > 0,
			mapsUrl: affichage.mapsUrl,
		}
	}
	let lignes = [
		[a.rue, a.numero].filter(Boolean).join(" "),
		[a.code_postal, a.localite].filter(Boolean).join(" "),
	].filter(Boolean)
	let titre = po.label || ""
	if (lignes.length === 0) {
		// GPS habillé (label = adresse reverse-géocodée) : coordonnées en
		// sous-ligne ; sinon les lignes d'affichage (texte brut legacy).
		lignes =
			coordsTexte && titre && titre !== coordsTexte
				? [coordsTexte]
				: affichage.lines.slice(titre ? 1 : 0)
	}
	if (!titre) {
		titre = lignes[0]
		lignes = lignes.slice(1)
	}
	// Un POI de base (type Parc, sans id) avec une adresse saisie est une
	// adresse (compat historique, cf. PointInteret.depuis_payload_mobile).
	const type =
		po.type === 0 && !po.id ? 4 : po.adresse || po.label ? po.type : null
	return {
		type: TYPE_LABELS[type] || null,
		titre,
		lignes,
		mapsUrl: affichage.mapsUrl,
	}
}
